import { createBrowserRouter, Outlet } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Logout } from '../components/screens/Logout/Logout'
import { GoogleLoginV2 } from '../components/screens/GoogleLoginV2/GoogleLoginV2'
import { AuthLayout } from '../components/screens/AuthLayout/AuthLayout'
import { ROUTES } from './routes'
import { Canvas } from '../components/screens/Canvas/Canvas'
import { Explorer } from '../components/screens/Explorer/Explorer'
import { CanvasDashboard } from '../components/screens/CanvasDashboard/CanvasDashboard'
import { ScoopAdmin } from '../components/screens/ScoopAdmin/ScoopAdmin'
import { Hubspot } from '../components/screens/Appconnect/Hubspot/Hubspot'
import { Salesforce } from '../components/screens/Appconnect/GMail/Salesforce'
import { DatasetIndex } from '../components/screens/Source/DatasetIndex/DatasetIndex'
import { DatasetInsight } from '../components/screens/Source/DatasetInsight/DatasetInsight'
import { LiveWorksheets } from '../components/screens/LiveWorksheets/LiveWorksheets'
import { Setup } from '../components/screens/Setup/Setup'
import { GMail } from '../components/screens/Appconnect/GMail/GMail'
import { Auth } from '../components/screens/Auth/Auth'
import { Recipe } from '../components/screens/Recipe/Recipe'
import { APIConnectorAuth } from '../components/screens/Appconnect/API/APIConnectorAuth'
import { Presentation } from '../components/Presentation/Presentation'
import { Questionnaire } from '../components/screens/Auth/Questionnaire/Questionnaire'
import { ScoopFallback } from '../components/screens/ScoopFallback/ScoopFallback'
import { ErrorBoundary } from 'react-error-boundary'
import { logError } from '../api/hooks/logs/api'
import { EVENTS, trackEvent } from '../events'
import { Account } from '../components/screens/Account/Account'
import { Insights } from '../components/screens/Insights/Insights'
import { Asset } from '../components/screens/Asset/Asset'
import { SummaryAsset } from '../components/screens/Asset/SummaryAsset'
import { ClusterAsset } from '../components/screens/Asset/ClusterAsset'
import { TreeAsset } from '../components/screens/Asset/TreeAsset/TreeAsset'
import { PublicSocketChat } from '../components/screens/SocketChat/PublicSocketChat/PublicSocketChat'
import { EmbeddedSocketChat } from '../components/screens/SocketChat/EmbeddedSocketChat/EmbeddedSocketChat'
import { AdminRoute } from './AdminRoute'
import { AcceptInvitation } from '../components/screens/AcceptInvitation/AcceptInvitation'
import { LiveQuery } from '../components/screens/LiveQuery/LiveQuery'
import { Success } from '../components/screens/BillingFlow/Success'
import { Cancel } from '../components/screens/BillingFlow/Cancel'
import { ForceLightTheme } from './ForceLightTheme'
import { ExplorerV2 } from '../components/screens/ExplorerV2/ExplorerV2'
import { DomainIntelligence } from '../components/screens/DomainIntelligence/DomainIntelligence'
import { DIDesignGallery } from '../components/screens/DIDesignGallery/DIDesignGallery'
import HelpBotLayout from '../components/HelpBot/components/HelpBotLayout/HelpBotLayout'

// Wrapper to remount Insights when workspace changes
const InsightsWithWorkspaceKey = () => {
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    return <Insights key={`insights-${workspaceID}`} />
}

export const router = createBrowserRouter([
    {
        element: (
            <ErrorBoundary
                FallbackComponent={ScoopFallback}
                onError={(error) => {
                    // Don't send error emails when running locally (localhost)
                    const isLocalhost = window.location.hostname === 'localhost'

                    if (process.env.REACT_APP_SCOOP_ENV !== 'dev' && !isLocalhost) {
                        logError(error.message, error.stack)
                        trackEvent(EVENTS.ERROR.OOPS, { message: error.message })
                    }
                }}
            >
                <Outlet />
            </ErrorBoundary>
        ),
        children: [
            {
                path: ROUTES.DEMO_CHAT_OPPORTUNITIES,
                element: <PublicSocketChat demoType="opportunities" />,
            },
            {
                path: ROUTES.DEMO_CHAT_HOUSING,
                element: <PublicSocketChat demoType="housing" />,
            },
            {
                path: ROUTES.EMBEDDED_CHAT,
                element: <EmbeddedSocketChat />,
            },
            {
                element: <ForceLightTheme />,
                children: [
                    {
                        path: ROUTES.LOGIN,
                        element: <Auth loginRoute={true} />,
                    },
                    {
                        path: ROUTES.SIGNUP,
                        element: <Auth loginRoute={false} />,
                    },
                ],
            },
            {
                path: ROUTES.ACCEPT_INVITATION,
                element: <AcceptInvitation />,
            },
            {
                path: ROUTES.RECIPE,
                element: <Recipe />,
            },
            {
                path: ROUTES.LOGOUT,
                element: <Logout />,
            },
            {
                path: ROUTES.GOOGLELOGIN,
                element: <GoogleLoginV2 />,
            },

            {
                element: <HelpBotLayout />,
                children: [
                    {
                        element: <ForceLightTheme />,
                        children: [
                            {
                                path: ROUTES.ONBOARDING,
                                element: <Questionnaire />,
                            },
                        ],
                    },
                    {
                        path: ROUTES.AUTH,
                        element: <AuthLayout />,
                        children: [
                            {
                                path: ROUTES.BILLING_SUCCESS,
                                element: <Success />,
                            },
                            {
                                path: ROUTES.BILLING_CANCEL,
                                element: <Cancel />,
                            },
                            {
                                path: ROUTES.CANVAS_DASHBOARD,
                                element: <CanvasDashboard />,
                            },
                            {
                                path: ROUTES.CANVAS,
                                element: <Canvas />,
                            },
                            {
                                path: ROUTES.EXPLORER,
                                element: <Explorer />,
                            },
                            {
                                path: ROUTES.EXPLORERV2,
                                element: (
                                    <AdminRoute>
                                        <ExplorerV2 />
                                    </AdminRoute>
                                ),
                            },
                            {
                                path: ROUTES.INSIGHTS,
                                element: <InsightsWithWorkspaceKey />,
                            },
                            {
                                path: ROUTES.SOURCES,
                                element: <DatasetIndex />,
                            },
                            {
                                path: ROUTES.LIVE_QUERY,
                                element: (
                                    <AdminRoute>
                                        <LiveQuery />
                                    </AdminRoute>
                                ),
                            },
                            {
                                path: ROUTES.DOMAIN_INTELLIGENCE,
                                element: (
                                    <AdminRoute>
                                        <DomainIntelligence />
                                    </AdminRoute>
                                ),
                            },
                            {
                                // Design-treatments gallery (target-state
                                // reference; unlinked, admin-gated)
                                path: ROUTES.DI_DESIGN_GALLERY,
                                element: (
                                    <AdminRoute>
                                        <DIDesignGallery />
                                    </AdminRoute>
                                ),
                            },
                            {
                                path: ROUTES.SCOOPADMIN,
                                element: (
                                    <AdminRoute>
                                        <ScoopAdmin />
                                    </AdminRoute>
                                ),
                            },
                            {
                                path: ROUTES.SOURCE,
                                element: <DatasetInsight />,
                            },
                            {
                                path: ROUTES.LIVE_WORKSHEETS,
                                element: <LiveWorksheets />,
                            },
                            {
                                path: ROUTES.PRESENTATION,
                                element: <Presentation />,
                            },
                            {
                                path: ROUTES.ACCOUNT,
                                element: <Account />,
                            },
                            // /embed-test/investigation removed 2026-07-04 (WS3.6): the embedded
                            // investigation prototype authenticated via a token in the URL query
                            // string (leaks via history/Referer/logs) — a banned identity pattern.
                            // Component deleted; its charts-from-numerics idea lives on in the
                            // portal's PeerBand. See DOMAIN_INTELLIGENCE_PORTAL_IMPLEMENTATION_PLAN.md WS3.6.
                        ],
                    },
                ],
            },
            {
                path: ROUTES.HUBSPOT,
                element: <Hubspot />,
            },
            {
                path: ROUTES.SALESFORCE,
                element: <Salesforce />,
            },
            {
                path: ROUTES.GMAIL,
                element: <GMail />,
            },
            {
                path: ROUTES.APICONNECTORAUTH,
                element: <APIConnectorAuth />,
            },
            {
                path: ROUTES.SETUP,
                element: <Setup />,
            },
            {
                path: ROUTES.ASSET,
                element: <Asset />,
                children: [
                    {
                        path: ROUTES.SUMMARY_ASSET,
                        element: <SummaryAsset />,
                    },
                    {
                        path: ROUTES.CLUSTER_ASSET,
                        element: <ClusterAsset />,
                    },
                    {
                        path: ROUTES.TREE_ASSET,
                        element: <TreeAsset />,
                    },
                ],
            },
            {
                path: '*',
                element: <ScoopFallback notFound />,
            },
        ],
    },
])
